import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownTrayIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, CloudArrowDownIcon,
  ExclamationTriangleIcon, MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useSyncWalkarounds, useWalkarounds } from '../hooks/useWalkarounds';
import {
  Note, PageHeader, Panel, States, Stat, num, pct, selectClass,
} from '../components/OpsPageKit';

// Walkarounds: who has done this week's vehicle walkaround check, and who has
// not.
//
// Starts from the Fleet Master (every van and its assigned driver), not from
// Samsara, because the question is who is MISSING - and a van with no
// walkaround does not appear in Samsara at all. Vans not expected to have one
// (off the road, sold, no driver) are set aside with the reason, and
// walkarounds on vans the Fleet Master does not list are shown at the foot.

const DAY = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const ukDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { timeZone: 'Europe/London' }) : '—');
const ukDateTime = (d) => (d ? new Date(d).toLocaleString('en-GB', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
}).replace(',', '') : '—');
const ymdToUk = (ymd) => String(ymd).split('-').reverse().join('/');
const shiftWeek = (monday, n) => {
  const [y, m, d] = monday.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 7 * n)).toISOString().slice(0, 10);
};
const daysAgo = (d) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null);
const typeLabel = (t) => (t === 'preTrip' ? 'Pre-trip' : t === 'postTrip' ? 'Post-trip' : t || '');

// Operations van / MEWP / Civils, from the Fleet Master's longer vehicle types.
const vehicleGroup = (t) => {
  const s = String(t || '').toLowerCase();
  if (s.includes('mewp')) return 'MEWP';
  if (s.includes('civils')) return 'Civils';
  if (s.includes('operations')) return 'Operations';
  return 'Other';
};

// The driver's region from Match Us; the Fleet Master home site only where that
// is missing (it is filled in for 16 of 236 vans).
const areaOf = (r) => r.region || r.home_site || 'No region';

function StatusPill({ row }) {
  if (!row.done) return <span className="inline-block rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">Not done</span>;
  if (row.unsafe) return <span className="inline-block rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">Done · unsafe</span>;
  return <span className="inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">Done</span>;
}

function downloadCsv(data) {
  const head = ['Status', 'Registration', 'Driver (Fleet Master)', 'Vehicle type', 'Fleet status', 'Region',
    ...data.week.days.map((d, i) => `${DAY[i]} ${ymdToUk(d.date)}`), 'Walkaround by', 'Done by someone else', 'Unsafe', 'Open defects', 'Last walkaround'];
  const lines = [head];
  data.rows.forEach((r) => lines.push([
    r.done ? 'Done' : 'Not done', r.registration, r.driver, r.vehicle_type, r.fleet_status, r.region || r.home_site,
    ...r.days, [...new Set(r.walkarounds.map((w) => w.by).filter(Boolean))].join(' / '),
    r.done_by_other ? 'Yes' : '', r.unsafe ? 'Yes' : '', r.open_defects || '', ukDate(r.last_walkaround),
  ]));
  const cell = (c) => { const s = c == null ? '' : String(c); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const url = URL.createObjectURL(new Blob([lines.map((r) => r.map(cell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `walkarounds-week-${data.week.iso_week}-${data.week.monday}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function Walkarounds() {
  const [week, setWeek] = useState(null);
  const [view, setView] = useState('missing');
  const [q, setQ] = useState('');
  const [site, setSite] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const [showNotExpected, setShowNotExpected] = useState(false);
  const [showUnmatched, setShowUnmatched] = useState(false);

  const { data, isLoading, isFetching, isError, refetch } = useWalkarounds(week);
  const sync = useSyncWalkarounds();

  const t = data?.totals || {};
  const monday = data?.week?.monday;

  const sites = useMemo(() => [...new Set((data?.rows || []).map(areaOf))].sort(), [data]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.rows || []).filter((r) => {
      if (view === 'missing' && r.done) return false;
      if (view === 'done' && !r.done) return false;
      if (view === 'attention' && !(r.unsafe || r.open_defects || r.done_by_other)) return false;
      if (site && areaOf(r) !== site) return false;
      if (vehicle && vehicleGroup(r.vehicle_type) !== vehicle) return false;
      if (needle && !`${r.registration} ${r.driver || ''}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [data, view, q, site, vehicle]);

  // Done rate per region (the driver's region in Match Us), lowest first - where to chase.
  const bySite = useMemo(() => {
    const m = new Map();
    (data?.rows || []).forEach((r) => {
      const k = areaOf(r);
      if (!m.has(k)) m.set(k, { site: k, expected: 0, done: 0 });
      const s = m.get(k); s.expected += 1; if (r.done) s.done += 1;
    });
    return [...m.values()].sort((a, b) => a.done / a.expected - b.done / b.expected || b.expected - a.expected);
  }, [data]);

  const toggle = (reg) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(reg)) next.delete(reg); else next.add(reg);
    return next;
  });

  const maxDay = Math.max(1, ...(t.per_day || []));
  const chip = (key, label, count) => (
    <button
      key={key}
      type="button"
      onClick={() => setView(key)}
      className={`rounded-full border px-3 py-1 text-sm font-medium ${view === key ? 'border-brand bg-brand text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-brand'}`}
    >
      {label} <span className={view === key ? 'text-white/80' : 'text-gray-400'}>{num(count)}</span>
    </button>
  );

  return (
    <div className="min-w-0 max-w-full p-4 sm:p-6">
      <Link to="/?dept=fleet" className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" /> Back to Fleet
      </Link>
      <PageHeader
        title="Walkarounds"
        subtitle="Weekly vehicle walkaround checks from Samsara, against the vans and drivers in the Fleet Master."
        onRefresh={refetch}
        fetching={isFetching}
      >
        <div className="flex w-full items-center rounded-md border border-gray-300 bg-white sm:inline-flex sm:w-auto">
          <button type="button" onClick={() => setWeek(shiftWeek(monday, -1))} disabled={!monday} className="p-1.5 text-gray-500 hover:text-brand disabled:opacity-40" aria-label="Previous week">
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <span className="flex-1 px-2 text-center text-sm font-medium tabular-nums text-gray-800">
            {data ? `Week ${data.week.iso_week} · ${ymdToUk(data.week.monday)} – ${ymdToUk(data.week.sunday)}` : 'Loading…'}
          </span>
          <button type="button" onClick={() => setWeek(shiftWeek(monday, 1))} disabled={!monday || data?.week?.is_current} className="p-1.5 text-gray-500 hover:text-brand disabled:opacity-40" aria-label="Next week">
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
        {data && !data.week.is_current ? (
          <button type="button" onClick={() => setWeek(null)} className="text-sm font-medium text-brand hover:underline">This week</button>
        ) : null}
        <button
          type="button"
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
          title="Pull the latest walkarounds from Samsara now"
        >
          <CloudArrowDownIcon className={`h-4 w-4 ${sync.isPending ? 'animate-pulse' : ''}`} />
          {sync.isPending ? 'Pulling…' : 'Update from Samsara'}
        </button>
        {data ? (
          <button type="button" onClick={() => downloadCsv(data)} className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:border-brand">
            <ArrowDownTrayIcon className="h-4 w-4" /> CSV
          </button>
        ) : null}
      </PageHeader>

      {sync.isError ? (
        <Note tone="red">{sync.error?.response?.data?.error || 'Could not update from Samsara.'}</Note>
      ) : null}

      <States isLoading={isLoading} isError={isError} what="walkarounds" />

      {data && (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Stat label="Vans expected" value={num(t.expected)} sub="assigned in Fleet Master" />
            <Stat label="Walkaround done" value={num(t.done)} sub={`${pct(t.done_rate)} of vans`} accent tone="#15803D" />
            <Stat label="Not done" value={num(t.missing)} sub={data.week.is_current ? 'so far this week' : 'that week'} tone={t.missing ? '#B91C1C' : undefined} />
            <Stat label="Unsafe / open defects" value={`${num(t.unsafe)} / ${num(t.open_defects)}`} sub="vans unsafe / defects open" tone={t.unsafe ? '#B45309' : undefined} />
            <div className="col-span-2 lg:col-span-1">
              <Stat label="Done by someone else" value={num(t.done_by_other)} sub="not the assigned driver" />
            </div>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <Panel title="Walkarounds by day">
              <div className="flex items-end gap-2 px-4 pb-3 pt-4" style={{ height: 150 }}>
                {data.week.days.map((d, i) => (
                  <div key={d.date} className="flex h-full flex-1 flex-col items-center justify-end gap-1">
                    <span className="text-xs font-semibold tabular-nums text-gray-700">{d.status === 'future' ? '' : num(t.per_day[i])}</span>
                    <div
                      className={`w-full rounded-t ${d.status === 'future' ? 'bg-gray-100' : 'bg-brand'}`}
                      style={{ height: `${d.status === 'future' ? 4 : Math.max(4, (t.per_day[i] / maxDay) * 90)}px` }}
                    />
                    <span className={`text-[11px] ${d.status === 'today' ? 'font-bold text-brand' : 'text-gray-500'}`}>{DAY[i]}</span>
                  </div>
                ))}
              </div>
            </Panel>
            <div className="lg:col-span-2">
              <Panel title="Done by region" right={<span className="text-xs text-gray-400">lowest first</span>}>
                <div className="max-h-48 overflow-y-auto px-4 py-2">
                  {bySite.map((s) => (
                    <button
                      key={s.site}
                      type="button"
                      onClick={() => setSite(site === s.site ? '' : s.site)}
                      className={`flex w-full items-center gap-3 rounded px-1 py-1 text-left text-sm hover:bg-gray-50 ${site === s.site ? 'bg-brand-light' : ''}`}
                    >
                      <span className="w-28 shrink-0 truncate text-gray-700 sm:w-44">{s.site}</span>
                      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                        <span className="block h-full rounded-full bg-brand" style={{ width: `${(s.done / s.expected) * 100}%` }} />
                      </span>
                      <span className="w-14 shrink-0 text-right tabular-nums text-gray-500 sm:w-20">{s.done}/{s.expected}</span>
                    </button>
                  ))}
                </div>
              </Panel>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {chip('missing', 'Not done', t.missing)}
            {chip('done', 'Done', t.done)}
            {chip('attention', 'Needs attention', (data.rows || []).filter((r) => r.unsafe || r.open_defects || r.done_by_other).length)}
            {chip('all', 'All vans', t.expected)}
            <div className="relative w-full sm:ml-auto sm:w-auto">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-gray-400" />
              <input id="walkaround-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Registration or driver…" className={`${selectClass} w-full pl-8 sm:w-56`} />
            </div>
            <select id="walkaround-site" value={site} onChange={(e) => setSite(e.target.value)} className={`${selectClass} min-w-0 flex-1 sm:flex-none`}>
              <option value="">All regions</option>
              {sites.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select id="walkaround-vehicle" value={vehicle} onChange={(e) => setVehicle(e.target.value)} className={`${selectClass} min-w-0 flex-1 sm:flex-none`}>
              <option value="">All vehicles</option>
              {['Operations', 'MEWP', 'Civils', 'Other'].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>

          <p className="mt-3 text-xs text-gray-400 sm:hidden">Swipe the table sideways to see the whole week. The van stays pinned on the left.</p>
          {/* The table scrolls sideways inside its own box - touch-contained so a
              swipe moves the week, not the page - with the van column pinned so
              you never lose which row you are on. */}
          <div className="mt-2 max-w-full overflow-x-auto overscroll-x-contain rounded-lg border border-gray-200 bg-white sm:mt-3" style={{ WebkitOverflowScrolling: 'touch' }}>
            <table className="w-full min-w-[860px] text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 shadow-[1px_0_0_#e5e7eb]">Van</th>
                  <th className="px-3 py-2">Driver (Fleet Master)</th>
                  <th className="px-3 py-2">Region</th>
                  {data.week.days.map((d, i) => (
                    <th key={d.date} className={`px-1.5 py-2 text-center ${d.status === 'today' ? 'text-brand' : ''}`}>
                      {DAY[i]}<span className="block text-[10px] font-normal normal-case">{ymdToUk(d.date).slice(0, 5)}</span>
                    </th>
                  ))}
                  <th className="px-3 py-2">Walkaround</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((r) => {
                  const isOpen = open.has(r.registration);
                  const last = r.walkarounds[r.walkarounds.length - 1];
                  const ago = daysAgo(r.last_walkaround);
                  return (
                    <Fragment key={r.registration}>
                      <tr
                        onClick={() => r.walkarounds.length && toggle(r.registration)}
                        className={`${r.walkarounds.length ? 'cursor-pointer' : ''} hover:bg-gray-50`}
                        aria-expanded={r.walkarounds.length ? isOpen : undefined}
                      >
                        <td className={`sticky left-0 z-10 whitespace-nowrap px-3 py-2 shadow-[1px_0_0_#e5e7eb] ${!r.done ? 'bg-red-50' : 'bg-white'}`}>
                          <span className="font-mono text-xs font-semibold text-gray-900">{r.registration}</span>
                          <span className="block text-xs text-gray-400">{vehicleGroup(r.vehicle_type)}{r.pool_van ? ' · pool van' : ''}</span>
                          <span className="mt-1 block"><StatusPill row={r} /></span>
                        </td>
                        <td className="px-3 py-2 text-gray-800">
                          {r.driver || '—'}
                          <span className="block text-xs text-gray-400">{r.fleet_status}</span>
                        </td>
                        <td className="px-3 py-2 text-gray-600">{r.region || r.home_site || '—'}</td>
                        {r.days.map((n, i) => (
                          <td key={data.week.days[i].date} className="px-1.5 py-2 text-center">
                            {n ? (
                              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-700">{n > 1 ? n : '✓'}</span>
                            ) : (
                              <span className={`text-xs ${data.week.days[i].status === 'future' ? 'text-gray-200' : 'text-gray-300'}`}>·</span>
                            )}
                          </td>
                        ))}
                        <td className="min-w-[220px] px-3 py-2 text-xs text-gray-600">
                          {last ? (
                            <span className="flex items-start gap-1.5">
                              {r.walkarounds.length ? (isOpen ? <ChevronDownIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" /> : <ChevronRightIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />) : null}
                              <span>
                                {ukDateTime(last.at)} · {typeLabel(last.type)}
                                <span className="block">
                                  by {last.by || 'unknown'}
                                  {r.done_by_other ? <span className="ml-1 font-semibold text-amber-700">(not the assigned driver)</span> : null}
                                </span>
                                {r.open_defects ? (
                                  <span className="mt-0.5 flex items-center gap-1 font-semibold text-amber-700">
                                    <ExclamationTriangleIcon className="h-3.5 w-3.5" /> {r.open_defects} open defect{r.open_defects === 1 ? '' : 's'}
                                  </span>
                                ) : null}
                              </span>
                            </span>
                          ) : (
                            <span className="text-gray-500">
                              {r.last_walkaround ? `Last one ${ukDate(r.last_walkaround)} (${ago} day${ago === 1 ? '' : 's'} ago)` : 'None on record'}
                            </span>
                          )}
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr className="bg-gray-50/70">
                          <td colSpan={11} className="px-3 py-3">
                            {/* Pinned to the visible width on a phone, so the detail is not
                                spread across the full scrolled table. */}
                            <div className="sticky left-3 grid w-[calc(100vw-4rem)] gap-2 sm:static sm:w-auto md:grid-cols-2">
                              {r.walkarounds.map((w) => (
                                <div key={`${w.at}-${w.by}`} className="rounded-md border border-gray-200 bg-white p-3 text-xs">
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="font-semibold text-gray-900">{ukDateTime(w.at)} · {typeLabel(w.type)}</span>
                                    <span className={`rounded-full px-2 py-0.5 font-semibold ${w.safety === 'unsafe' ? 'bg-red-50 text-red-700' : w.safety === 'resolved' ? 'bg-sky-50 text-sky-700' : 'bg-emerald-50 text-emerald-700'}`}>
                                      {w.safety === 'unsafe' ? 'Unsafe' : w.safety === 'resolved' ? 'Defects resolved' : 'Safe'}
                                    </span>
                                  </div>
                                  <p className="mt-1 text-gray-600">Driver: {w.by || 'unknown'}{w.mechanic ? ` · Mechanic: ${w.mechanic}` : ''}</p>
                                  {w.defect_list.length ? (
                                    <ul className="mt-2 space-y-1">
                                      {w.defect_list.map((x, i) => (
                                        <li key={`${x.type}-${i}`} className="flex gap-2">
                                          <span className={`shrink-0 rounded px-1.5 font-medium ${x.resolved ? 'bg-gray-100 text-gray-500' : 'bg-amber-50 text-amber-800'}`}>
                                            {x.resolved ? 'Resolved' : 'Open'}
                                          </span>
                                          <span className="text-gray-700">
                                            <span className="font-medium">{x.type || 'Defect'}</span>{x.comment ? ` - ${x.comment}` : ''}
                                            {x.created_at ? <span className="text-gray-400"> · raised {ukDate(x.created_at)}</span> : null}
                                            {x.resolved_at ? <span className="text-gray-400"> · fixed {ukDate(x.resolved_at)}</span> : null}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : <p className="mt-1 text-gray-400">No defects reported.</p>}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
                {!filtered.length ? (
                  <tr><td colSpan={11} className="px-3 py-8 text-center text-gray-400">
                    {view === 'missing' ? 'Every expected van has had a walkaround this week.' : 'No vans match these filters.'}
                  </td></tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <button type="button" onClick={() => setShowNotExpected((v) => !v)} className="flex w-full items-center justify-between px-4 py-2.5 text-left">
              <span className="text-sm font-semibold text-gray-700">Not expected this week <span className="font-normal text-gray-400">{num(data.not_expected.length)} vans</span></span>
              {showNotExpected ? <ChevronDownIcon className="h-4 w-4 text-gray-400" /> : <ChevronRightIcon className="h-4 w-4 text-gray-400" />}
            </button>
            {showNotExpected ? (
              <div className="max-h-80 overflow-auto border-t border-gray-100">
                <table className="min-w-full text-sm">
                  <tbody className="divide-y divide-gray-100">
                    {data.not_expected.map((v) => (
                      <tr key={v.registration}>
                        <td className="whitespace-nowrap px-4 py-1.5 font-mono text-xs font-semibold">{v.registration}</td>
                        <td className="px-4 py-1.5 text-gray-600">{v.reason}</td>
                        <td className="px-4 py-1.5 text-gray-500">{v.driver || '—'}</td>
                        <td className="px-4 py-1.5 text-xs text-gray-400">{v.walkarounds ? `${v.walkarounds} walkaround${v.walkarounds === 1 ? '' : 's'} anyway` : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>

          <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <button type="button" onClick={() => setShowUnmatched((v) => !v)} className="flex w-full items-center justify-between px-4 py-2.5 text-left">
              <span className="text-sm font-semibold text-gray-700">Walkarounds on vans not in the Fleet Master <span className="font-normal text-gray-400">{num(data.unmatched.length)}</span></span>
              {showUnmatched ? <ChevronDownIcon className="h-4 w-4 text-gray-400" /> : <ChevronRightIcon className="h-4 w-4 text-gray-400" />}
            </button>
            {showUnmatched ? (
              <div className="max-h-80 overflow-auto border-t border-gray-100">
                <table className="min-w-full text-sm">
                  <tbody className="divide-y divide-gray-100">
                    {data.unmatched.map((w) => (
                      <tr key={`${w.vehicle}-${w.at}`}>
                        <td className="whitespace-nowrap px-4 py-1.5 font-mono text-xs font-semibold">{w.vehicle || '—'}</td>
                        <td className="px-4 py-1.5 text-gray-600">{ukDateTime(w.at)} · {typeLabel(w.type)}</td>
                        <td className="px-4 py-1.5 text-gray-500">{w.by || '—'}</td>
                      </tr>
                    ))}
                    {!data.unmatched.length ? <tr><td className="px-4 py-3 text-xs text-gray-400">Every walkaround this week matched a Fleet Master van.</td></tr> : null}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>

          <Note tone="brand">
            <p>
              A van counts as <strong>done</strong> when Samsara has at least one walkaround for it between Monday 00:00 and Sunday 23:59
              (UK time), matched on registration. <strong>Expected</strong> vans are those with a driver in the Fleet Master and a status other
              than Returned, Removed, Sold, Available, Breakdown or Awaiting Initial Inspection. Walkarounds update from Samsara every hour at :20
              {data.sync.last_sync ? `, last at ${ukDateTime(data.sync.last_sync)}` : ''}; history starts {ukDate(data.sync.history_from)}.
            </p>
          </Note>
        </>
      )}
    </div>
  );
}

export default Walkarounds;
